import { Search } from 'lucide-react';
import { useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '../../lib/utils.ts';
import { Input } from './input.tsx';
import { Popover } from './popover.tsx';
import { Icon } from './icon.tsx';

/** Eine Zeile im Suchmenü; `content` ist frei (Farbpunkt, Name, Häkchen, Unterzeile …). */
export interface SearchMenuOption {
  key: string;
  content: ReactNode;
  /** Gewählt (Häkchen-Menüs, `aria-selected`); ohne Angabe = gerade markierte Zeile. */
  selected?: boolean;
  className?: string;
  testId?: string;
}

/**
 * Suchmenü (Story 23.4/23.12): Knopf öffnet ein Popover (am Handy als Blatt) mit Suchfeld und Liste. ↑/↓ markieren,
 * Enter wählt, Esc schließt, Maus markiert beim Darüberfahren. Filtern und Wählen macht der Aufrufer (`query`,
 * `options`, `onChoose`); Meldungen unter der Liste (leer, kein Treffer, Fehler) kommen als `children`.
 */
export function SearchMenu({
  open,
  onOpenChange,
  label,
  trigger,
  query,
  onQueryChange,
  searchLabel,
  maxLength,
  options,
  onChoose,
  listLabel,
  multiselectable = false,
  className,
  listClassName,
  testId,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Name des Popovers (Dialog am Handy). */
  label: string;
  trigger: Parameters<typeof Popover>[0]['trigger'];
  query: string;
  onQueryChange: (query: string) => void;
  /** Name und Platzhalter des Suchfelds. */
  searchLabel: string;
  maxLength?: number;
  options: SearchMenuOption[];
  onChoose: (index: number) => void;
  listLabel: string;
  multiselectable?: boolean;
  className?: string;
  listClassName?: string;
  testId?: string;
  children?: ReactNode;
}) {
  const [active, setActive] = useState(0);
  const listId = useId();
  const optionId = (i: number) => `${listId}-o${i}`;
  const count = options.length;
  const activeIndex = count === 0 ? -1 : Math.min(active, count - 1);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (count === 0) return;
      setActive((activeIndex + (e.key === 'ArrowDown' ? 1 : -1) + count) % count);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0) onChoose(activeIndex);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        if (!v) setActive(0);
        onOpenChange(v);
      }}
      label={label}
      className={cn('w-popover-w p-space-2', className)}
      trigger={trigger}
    >
      <div className="flex flex-col gap-space-2" data-testid={testId}>
        <div className="relative">
          <Icon icon={Search} size="base" stroke="strong" aria-hidden className="pointer-events-none absolute top-1/2 left-space-3 -translate-y-1/2 text-ink-3" />
          <Input
            role="combobox"
            aria-expanded
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
            aria-label={searchLabel}
            placeholder={searchLabel}
            value={query}
            maxLength={maxLength}
            onChange={(e) => {
              onQueryChange(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            className="h-10 pr-space-3 pl-space-8 max-tablet:h-11 max-tablet:text-field-phone"
          />
        </div>
        <ul
          id={listId}
          role="listbox"
          aria-multiselectable={multiselectable || undefined}
          aria-label={listLabel}
          className={cn('m-0 flex max-h-72 list-none flex-col overflow-y-auto p-0', listClassName)}
        >
          {options.map((o, i) => (
            <li
              key={o.key}
              id={optionId(i)}
              role="option"
              aria-selected={o.selected ?? i === activeIndex}
              data-testid={o.testId}
              onPointerMove={() => setActive(i)}
              onClick={() => onChoose(i)}
              className={cn(
                'flex min-h-10 cursor-pointer items-center gap-space-3 rounded-tile px-space-3 text-row text-ink pointer-coarse:min-h-11',
                i === activeIndex && 'bg-tile',
                o.className,
              )}
            >
              {o.content}
            </li>
          ))}
        </ul>
        {children}
      </div>
    </Popover>
  );
}

/** Graue Meldung unter der Liste („Kein Label passt.“); `alert` für Fehler. */
export function SearchMenuNote({ children, alert = false }: { children: ReactNode; alert?: boolean }) {
  return (
    <p role={alert ? 'alert' : undefined} className={cn('m-0 px-space-3 pb-space-1 text-body', alert ? 'text-clarify' : 'text-ink-3')}>
      {children}
    </p>
  );
}
